import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { EventItem, EventName } from '@/api/types';
import { formatDateTime } from '@/lib/format';
import { toPlayerId } from '@/lib/player-id';
import { Badge } from '@/ui/Badge';
import { Select, type SelectOption } from '@/ui/Select';

const EVENT_OPTIONS: SelectOption<EventName | ''>[] = [
  { value: '', label: 'All events' },
  { value: 'issued', label: 'Invited' },
  { value: 'redeemed', label: 'Joined' },
];

export function RecentActivity({ events, titles }: { events: EventItem[]; titles: Map<string, string> }) {
  const [eventName, setEventName] = useState<EventName | ''>('');
  const [agent, setAgent] = useState('');

  const agents = useMemo(() => [...new Set(events.map((event) => event.agentId))].sort(), [events]);
  const rows = useMemo(
    () => events.filter((event) => (!eventName || event.event === eventName) && (!agent || event.agentId === agent)),
    [events, eventName, agent],
  );

  if (events.length === 0) return <p className="text-sm text-slate-500">Nothing recorded yet.</p>;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <Select label="Event" value={eventName} options={EVENT_OPTIONS} onChange={(value) => setEventName(value)} />
        {agents.length > 1 && (
          <Select
            label="Agent"
            value={agent}
            options={[{ value: '', label: 'All agents' }, ...agents.map((value) => ({ value, label: value }))]}
            onChange={setAgent}
          />
        )}
      </div>
      {rows.length === 0 && <p className="text-sm text-slate-500">No recent event matches the filters.</p>}
      <ul className="divide-y divide-slate-100 text-sm">
        {rows.map((event) => (
          <li
            key={`${event.linkId}-${event.event}-${event.occurredAt}`}
            className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2"
          >
            <span className="w-36 shrink-0 text-slate-500">{formatDateTime(event.occurredAt)}</span>
            <Badge value={event.event} />
            {event.playerUuid === null ? (
              <Badge value="open link" />
            ) : (
              <Link
                to={`/tournaments/${encodeURIComponent(event.tournamentId)}?player=${encodeURIComponent(event.playerUuid)}`}
                className="font-mono text-xs text-blue-700 hover:underline"
              >
                {toPlayerId(event.playerUuid)}
              </Link>
            )}
            <span className="text-slate-500">by {event.agentId}</span>
            <Link
              to={`/tournaments/${encodeURIComponent(event.tournamentId)}`}
              className="text-blue-700 hover:underline"
            >
              {titles.get(event.tournamentId) || event.tournamentId}
            </Link>
            {event.alreadyJoined && <span className="text-xs text-slate-500">(was already in)</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
